import { useMidi } from '@tunasong/audio-ui'
import type { MidiNoteEvent } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { midiInsert } from './melody-midi-insert.js'

/**
 * Insert into the melody from a MIDI keyboard, one note per key press at the cursor.
 *
 * This is step entry, not capture: `noteoff` is ignored and the note takes the grid's length.
 * Holding a key to set a duration is a timing measurement, and timing measurement against a click
 * is sub-project 2's job (§5) - a rough version of it here would put unquantized durations into an
 * entity whose whole contract is that onsets sit on the grid.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface UseMelodyMidiInsertProps {
  melody: Melody
  cursorBeats: number
  onChange: (melody: Melody) => void
  onCursorChange: (beats: number) => void
  /** Off on a read-only roll. */
  enabled?: boolean
}

export const useMelodyMidiInsert = (props: UseMelodyMidiInsertProps): void => {
  const { melody, cursorBeats, onChange, onCursorChange, enabled = true } = props

  const handleNoteEvent = (event: MidiNoteEvent) => {
    if (!enabled || event.type !== 'noteon') {
      return
    }
    const midi = event.note.number
    if (typeof midi !== 'number') {
      return
    }
    const result = midiInsert(melody, cursorBeats, midi, event.value ?? MelodyLib.SUGGESTION_VELOCITY)
    onChange(result.melody)
    onCursorChange(result.cursorBeats)
  }

  useMidi({ onNoteEvent: handleNoteEvent })
}
